import React, {
  useRef,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import debounce from "lodash.debounce";
import styles from "./ClassesScreen.module.scss";
import {
  AddButton,
  Chip,
  ModalNewClass,
  SearchInput,
  Card,
  Modal,
  ModalStudentsAttending,
} from "../../components";
import { ReactComponent as Pen } from "../../assets/icons/pen.svg";
import { ReactComponent as Delete } from "../../assets/icons/delete.svg";
import { notifyError, notifyInfo } from "../../utils/notify";
import {
  selectClasses,
  selectDeleteClassPending,
} from "../../store/slices/class/slice";
import {
  fetchClasses,
  deleteClass,
} from "../../store/slices/class/asyncThunks";
import { deletePeriod } from "../../store/slices/period/asyncThunks";

import { selectLoggedInUserRole } from "../../store/slices/user/slice";
import { ROLES } from "../../utils/constants";

const ClassesScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userRole = useSelector(selectLoggedInUserRole);
  const deleteClassPending = useSelector(selectDeleteClassPending);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const { data } = useSelector(selectClasses);

  const [dataForRender, setDataForRender] = useState([]);

  const newClassModal = useRef();
  const deleteModalRef = useRef();
  const deletePeriodModalRef = useRef();
  const refModalStudentsAtt = useRef();

  useEffect(() => {
    dispatch(fetchClasses({}));
  }, [dispatch]);

  useEffect(() => {
    setDataForRender(data);
  }, [data]);

  const editClass = (item) => {
    setSelectedClass(item);
    newClassModal.current.show();
  };

  const showDeleteModal = (classObj) => {
    setSelectedClass(classObj);
    deleteModalRef.current.show();
  };

  const showDeeletePeriodModal = (period) => {
    setSelectedPeriod(period);
    deletePeriodModalRef.current.show();
  };

  const removePeriod = () => {
    dispatch(deletePeriod(selectedPeriod.id)).then((promise) => {
      if (promise.type.includes("fulfilled")) {
        notifyInfo("Successfully deleted period");
        deletePeriodModalRef.current.hide();
        dispatch(fetchClasses({}));
      } else {
        notifyError(promise.error.message);
      }
    });
  };

  const removeClass = () => {
    dispatch(deleteClass(selectedClass.classId)).then((promise) => {
      if (promise.type.includes("fulfilled")) {
        notifyInfo("Successfully deleted class");
        deleteModalRef.current.hide();
      } else {
        notifyError(promise.error.message);
      }
    });
  };

  const changeHandler = useCallback(
    (text) => {
      if (data.classes && text.length > 0) {
        const returnValue = data.classes.filter((el) =>
          el.name.toLowerCase().includes(text.toLowerCase())
        );
        setDataForRender({ classes: returnValue });
      } else if (text.length === 0) {
        setDataForRender(data);
      }
    },
    [data]
  );

  const debouncedChangeHandler = useMemo(
    () => debounce(changeHandler, 300),
    [changeHandler]
  );

  return (
    <div className={styles.container}>
      <div className={styles.headerContainer}>
        <h1 className={styles.headerTitle}>Classes</h1>
      </div>
      <div className={styles.searchContainer}>
        <SearchInput
          placeholder="Search Classes"
          onChange={debouncedChangeHandler}
        />
        {ROLES.ADMIN === userRole && (
          <AddButton
            aria-label="Add Class"
            title="Add Class"
            onClick={() => {
              setSelectedClass(null);
              newClassModal.current.show();
            }}
          />
        )}
      </div>
      {dataForRender.classes &&
        dataForRender.classes.map((item, index) => {
          return (
            <div key={index} className={styles.classContainer}>
              <div className={styles.classContainerName}>
                <div className={styles.classContainerNameTitle}>
                  <h2>{item.name}</h2>
                  {ROLES.ADMIN === userRole && (
                    <Pen
                      className={styles.editButton}
                      onClick={() => editClass(item)}
                    />
                  )}
                  {ROLES.ADMIN === userRole && (
                    <Delete
                      className={styles.deleteButton}
                      onClick={() => showDeleteModal(item)}
                    />
                  )}
                </div>
                {ROLES.ADMIN === userRole && (
                  <AddButton
                    className={styles.greenBackground}
                    aria-label="Add Period"
                    title="Add Period"
                    onClick={() =>
                      navigate({
                        pathname: "period/" + item.classId,
                        search: `?page=${1}`,
                      })
                    }
                  />
                )}
              </div>

              <div className={styles.classPerriodContainer}>
                {item.periods &&
                  item.periods.map((period, index) => {
                    return (
                      <Card key={index} className={styles.classPerriodCard}>
                        <div className={styles.classPerriodCardContainer}>
                          <h3 className={styles.classPerriodCardContainerTitle}>
                            {period.name}
                          </h3>
                          <div
                            className={
                              styles.classPerriodCardContainerAssistent
                            }
                          >
                            <span
                              className={
                                styles.classPerriodCardContainerAssistentTitle
                              }
                            >
                              Assistent:
                            </span>
                            <span
                              className={
                                styles.classPerriodCardContainerAssistentName
                              }
                            >
                              {period.user.firstName +
                                " " +
                                period.user.lastName}
                            </span>
                          </div>
                          <div
                            className={styles.classPerriodCardContainerPeople}
                          >
                            <div
                              className={styles.chipContainer}
                              onClick={() => {
                                setSelectedStudents(period.studentPeriods);
                                refModalStudentsAtt.current.show();
                              }}
                            >
                              {period.studentPeriods.length < 4 &&
                                period.studentPeriods.map((student, index) => {
                                  return (
                                    <Chip
                                      key={index}
                                      firstName={student.user?.firstName[0]}
                                      lastName={student.user?.lastName[0]}
                                    />
                                  );
                                })}
                            </div>
                            {period.studentPeriods.length >= 4 && (
                              <div
                                className={styles.chipContainer}
                                onClick={() => {
                                  setSelectedStudents(period.studentPeriods);
                                  refModalStudentsAtt.current.show();
                                }}
                              >
                                <Chip
                                  firstName={
                                    period.studentPeriods[0].user?.firstName[0]
                                  }
                                  lastName={
                                    period.studentPeriods[0].user?.lastName[0]
                                  }
                                />
                                <Chip
                                  firstName={
                                    period.studentPeriods[1].user?.firstName[0]
                                  }
                                  lastName={
                                    period.studentPeriods[1].user?.lastName[0]
                                  }
                                />
                                <Chip
                                  firstName={
                                    period.studentPeriods[2].user?.firstName[0]
                                  }
                                  lastName={
                                    period.studentPeriods[2].user?.lastName[0]
                                  }
                                />
                                <span className={styles.restNumber}>
                                  {period.studentPeriods.length - 3 + "+"}
                                </span>
                              </div>
                            )}
                            <div>
                              {ROLES.ADMIN === userRole && (
                                <Delete
                                  className={styles.deleteButton}
                                  onClick={() => showDeeletePeriodModal(period)}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
              </div>
            </div>
          );
        })}
      {ROLES.ADMIN === userRole && (
        <ModalNewClass
          selectedClass={selectedClass}
          modalRef={newClassModal}
          setSelectedClass={setSelectedClass}
        />
      )}
      <Modal
        ref={deleteModalRef}
        title="Delete Class"
        buttonOkText="DELETE"
        buttonCloseText="CANCEL"
        buttonOkOnClick={removeClass}
        buttonCloseOnClick={() => deleteModalRef.current.hide()}
        onClose={() => deleteModalRef.current.hide()}
        pendingAction={deleteClassPending}
      >
        <div>Are you sure you want to delete class: {selectedClass?.name}?</div>
      </Modal>

      <Modal
        ref={deletePeriodModalRef}
        title="Delete Period"
        buttonOkText="DELETE"
        buttonCloseText="CANCEL"
        buttonOkOnClick={removePeriod}
        buttonCloseOnClick={() => deletePeriodModalRef.current.hide()}
        onClose={() => deletePeriodModalRef.current.hide()}
      >
        <div>
          Are you sure you want to delete period: {selectedPeriod?.name}?
        </div>
      </Modal>
      <ModalStudentsAttending
        title="List of students"
        modalRef={refModalStudentsAtt}
        students={selectedStudents}
      />
    </div>
  );
};

export default ClassesScreen;
